import type { SubscriptionState } from "./subscription-access";

/**
 * Libellé d'état d'un abonnement — SOURCE UNIQUE des écrans.
 *
 * Le calcul était recopié dans `SubscriptionStateBadge` et `QuotaExhaustedBanner`, et les deux
 * copies avaient DÉJÀ divergé : le bandeau d'accueil annonçait « Résilié » à un compte en
 * impayé, là où l'étiquette disait « Paiement en attente ». C'est le motif que tout ce chantier
 * cherche à supprimer — une règle recopiée finit toujours par dire deux choses différentes.
 *
 * `t` est passé par l'appelant (le hook `useTranslation` ne s'utilise que dans un composant).
 */
export interface EtatAffiche {
  state: SubscriptionState;
  isOpen: boolean;
  /** Fin de la période payée, déjà formatée pour l'affichage. */
  finFormatee: string | null;
  /** Date de reprise d'une pause, déjà formatée. */
  repriseFormatee: string | null;
}

export function libelleEtatAbonnement(
  etat: EtatAffiche,
  t: (cle: string, options?: Record<string, unknown>) => string,
): string {
  const { state, isOpen, finFormatee, repriseFormatee } = etat;

  if (state === "paused") {
    if (repriseFormatee) return t("account.subscriptionPausedUntil", { date: repriseFormatee });
    if (isOpen && finFormatee) return t("account.subscriptionPausedAccessUntil", { date: finFormatee });
    return t("account.subscriptionPaused");
  }

  if (!isOpen) {
    return finFormatee
      ? t("account.subscriptionEndedOn", { date: finFormatee })
      : t("account.subscriptionEnded");
  }

  if (state === "unpaid") {
    return finFormatee
      ? t("account.subscriptionUnpaidAccessUntil", { date: finFormatee })
      : t("account.subscriptionUnpaid");
  }

  return finFormatee
    ? t("account.subscriptionCancelledAccessUntil", { date: finFormatee })
    : t("account.subscriptionCancelled");
}

/** Date lisible dans la langue de l'interface. Utilisée par tous les écrans d'état. */
export function formatDateAbonnement(
  valeur: string | Date | null | undefined,
  langue: string,
): string | null {
  if (!valeur) return null;
  const d = valeur instanceof Date ? valeur : new Date(valeur);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(langue === "en" ? "en-GB" : "fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
